import React, { useEffect } from 'react';
import { Modal, Button, Space } from 'components';
import { useBreakpoint } from 'hooks';
import { AuthContext } from 'contexts';
import { StyledSubHeader, StyledSteps } from '../StyledComponents';
import { SaveListing } from '../components';
import { ListingFormState, PageView } from '../types';

type CreateListingStepsProps = {
  listing: ListingFormState;
  listingId?: string;
  currentView: PageView;
  setCurrentView: (newView: PageView) => void;
  onSaveNewListing: ({
    moveToNextStep,
    forceDraft,
  }: {
    moveToNextStep?: boolean;
    forceDraft?: boolean;
  }) => void;
  deleteListing: () => Promise<void>;
  saved: boolean;
  loading: boolean;
  isBeingPublished?: boolean;
  documents: any[];
  distributeListing: () => Promise<void>;
  submitListingForReview: () => Promise<void>;
  isFormValid: boolean;
  agreedToTerms: boolean;
  photos?: any[];
  attachments?: any[];
  documentListError: string;
  uploading: boolean;
};

const CreateListingSteps: React.FC<CreateListingStepsProps> = ({
  listing,
  listingId,
  currentView,
  setCurrentView,
  onSaveNewListing,
  deleteListing,
  saved,
  loading,
  isBeingPublished,
  distributeListing,
  submitListingForReview,
  isFormValid,
  documentListError,
  documents,
  agreedToTerms,
  photos,
  attachments,
  uploading,
}) => {
  const { md } = useBreakpoint();
  const { auth, hasFeature } = React.useContext(AuthContext);

  useEffect(() => () => Modal.destroyAll(), []);

  const getPageViews = () => {
    const PAGE_VIEWS = [
      PageView.Documents,
      PageView.Details,
      PageView.Visibility,
    ];
    return !hasFeature('CREATE_LISTING') ? PAGE_VIEWS.slice(1) : PAGE_VIEWS;
  };

  const getPageViewFromStep = (step: number) => getPageViews()[step];

  const getCurrentStep = () =>
    getPageViews().findIndex(pageView => pageView === currentView);

  const handleStepChange = (stepTo: number) => {
    if (documentListError) return;

    const newView = getPageViewFromStep(stepTo);

    if (newView === PageView.Visibility && (!isFormValid || !saved)) {
      return;
    }

    setCurrentView(newView);
  };

  const handleNextClick = () => {
    if (auth?.isAdmin && documents.length === 0) {
      return Modal.confirm({
        title:
          'Are you sure you want to create this listing without listing documents?',
        onOk: () => setCurrentView(PageView.Details),
        okText: 'Continue',
        cancelText: 'Cancel',
      });
    }
    setCurrentView(PageView.Details);
  };

  return (
    <StyledSubHeader $md={md}>
      {md && (
        <StyledSteps
          onChange={handleStepChange}
          current={getCurrentStep()}
          size="small"
          dataCy="create-listing-steps"
        >
          {hasFeature('CREATE_LISTING') && (
            <StyledSteps.Step
              title="Upload Documents"
              dataCy="create-listing-upload-documents-step"
            />
          )}
          <StyledSteps.Step
            title="Add Details"
            dataCy="create-listing-add-details-step"
          />
          <StyledSteps.Step
            title={`${auth.isAdmin ? 'Publish' : 'Submit for Review'}`}
            dataCy="create-listing-final-step"
          />
        </StyledSteps>
      )}
      <Space>
        <SaveListing
          photos={photos}
          attachments={attachments}
          listing={listing}
          listingId={listingId}
          loading={loading}
          onSaveNewListing={onSaveNewListing}
          deleteListing={deleteListing}
          saved={saved}
          isBeingPublished={isBeingPublished}
          currentView={currentView}
          uploading={uploading}
        />
        {currentView === PageView.Documents && (
          <Button
            onClick={handleNextClick}
            disabled={Boolean(documentListError)}
            type="primary"
          >
            Next
          </Button>
        )}
        {currentView === PageView.Details && (
          <Button
            type="primary"
            onClick={() =>
              onSaveNewListing({ moveToNextStep: true, forceDraft: true })
            }
            disabled={loading || !isFormValid || uploading}
            data-cy="next-label"
          >
            Next
          </Button>
        )}
        {currentView === PageView.Visibility && (
          <Button
            type="primary"
            onClick={auth?.isAdmin ? distributeListing : submitListingForReview}
            data-cy="next-label"
            disabled={
              loading || (!agreedToTerms && listing.visibility === 'network')
            }
          >
            {auth?.isAdmin ? 'Done' : 'Submit for Review'}
          </Button>
        )}
      </Space>
    </StyledSubHeader>
  );
};

export default CreateListingSteps;
